/**
 * A customer's iOS signing material.
 *
 * Every app goes to its own customer's App Store account, so this is somebody else's
 * distribution certificate. Two files and a password; the team and the profile's name are read
 * out of the profile rather than asked for, because they are in there.
 *
 * The pair is checked before it is stored, so a wrong password or a profile for another app is
 * refused here instead of failing a build on a Mac ten minutes later.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type Client, type IosSigning as Stored } from '../api';

function expiry(iso: string | null): { text: string; warn: boolean } {
  if (!iso) return { text: 'no expiry recorded', warn: false };
  const days = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return { text: `expired ${-days} days ago`, warn: true };
  if (days < 30) return { text: `expires in ${days} days`, warn: true };
  return { text: `expires ${new Date(iso).toLocaleDateString()}`, warn: false };
}

export function IosSigning({ client, isAdmin, onToast, onChanged }: {
  client: Client;
  isAdmin: boolean;
  onToast: (message: string) => void;
  onChanged: () => void;
}) {
  const [stored, setStored] = useState<Stored | null>(null);
  const [replacing, setReplacing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const reload = useCallback(async () => {
    try {
      const found = await api.iosSigning(client.slug);
      setStored(found.present === false ? null : found);
    } catch {
      setStored(null);
    }
  }, [client.slug]);

  useEffect(() => { void reload(); }, [reload]);

  const form = (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        setErrors([]);
        setBusy(true);
        api.uploadIosSigning(client.slug, new FormData(event.currentTarget))
          .then((result) => {
            onToast(`Signing as ${result.teamId}`);
            setReplacing(false);
            void reload();
            onChanged();
          })
          .catch((failure: ApiError) => setErrors(failure.errors?.length ? failure.errors : [failure.message]))
          .finally(() => setBusy(false));
      }}
    >
      {errors.length > 0 && (
        <div className="errors">{errors.map((message) => <span key={message}>{message}</span>)}</div>
      )}
      <div className="field">
        <label htmlFor={`cert-${client.slug}`}>Distribution certificate</label>
        <input id={`cert-${client.slug}`} name="certificate" type="file" accept=".p12,.pfx" required />
        <span className="hint">A .p12 exported with its private key.</span>
      </div>
      <div className="field">
        <label htmlFor={`cert-pw-${client.slug}`}>Certificate password</label>
        <input id={`cert-pw-${client.slug}`} name="password" type="password" />
      </div>
      <div className="field">
        <label htmlFor={`prof-${client.slug}`}>Provisioning profile</label>
        <input id={`prof-${client.slug}`} name="profile" type="file" accept=".mobileprovision" required />
        <span className="hint">
          App Store distribution, for <span className="mono">{client.packageName}</span>.
        </span>
      </div>
      <div className="actions">
        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Checking' : stored ? 'Replace' : 'Store it'}
        </button>
        {stored && (
          <button type="button" className="btn quiet" onClick={() => setReplacing(false)}>Cancel</button>
        )}
      </div>
    </form>
  );

  if (!stored) {
    return (
      <section className="section">
        <div className="section-head">
          <h2>iOS signing</h2>
          <span className="note">Without it, builds produce screenshots and no .ipa</span>
        </div>
        {form}
      </section>
    );
  }

  const when = expiry(stored.expires);
  return (
    <section className="section">
      <div className="section-head">
        <h2>iOS signing</h2>
        <span className="note">{stored.certificateFile}</span>
      </div>

      <dl className="facts">
        <div className="fact prose">
          <dt>Profile</dt>
          <dd>{stored.profileName}</dd>
        </div>
        <div className="fact">
          <dt>Team</dt>
          <dd>{stored.teamId}</dd>
        </div>
        <div className="fact prose">
          <dt>Certificate</dt>
          <dd>{stored.certificateName ?? 'not read'}</dd>
        </div>
        <div className="fact prose">
          <dt>Validity</dt>
          <dd className={when.warn ? 'is-wrong' : ''}>{when.text}</dd>
        </div>
      </dl>

      {when.warn && (
        <div className="warnings">
          <h3>Worth a look</h3>
          <p className="warning">
            Regenerate the profile in the Apple Developer portal and upload the new one. A build
            signed with an expired profile is rejected at upload.
          </p>
        </div>
      )}

      {isAdmin && !replacing && (
        <div className="actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn quiet" onClick={() => setReplacing(true)}>
            Replace
          </button>
        </div>
      )}
      {replacing && <div style={{ marginTop: 18 }}>{form}</div>}
    </section>
  );
}
